import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, setUserFromCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const processAuth = async (sessionId, attemptNum = 1) => {
    try {
      const user = await login(sessionId);
      setUserFromCallback(user);

      // Clear the URL fragment + saved session id (single-use, can't be replayed)
      window.history.replaceState(null, '', window.location.pathname);
      sessionStorage.removeItem('pending_session_id');

      await new Promise(r => setTimeout(r, 100));
      const destination = user.role === 'investor' ? '/investor' : '/dashboard';
      navigate(destination, { replace: true });
    } catch (err) {
      const status = err.response?.status;

      // 403 = not authorized (no retry — restart OAuth would still 403)
      if (status === 403) {
        setError(err.response.data?.detail || 'Your email is not authorized to access this application.');
        return;
      }

      // 401 = session_id is invalid or already-consumed.
      // Emergent OAuth session IDs are SINGLE-USE — retrying with the same id
      // hits the same wall every time, so don't auto-retry. Tell the user to
      // restart the sign-in flow.
      if (status === 401) {
        sessionStorage.removeItem('pending_session_id');
        setError('Your sign-in link expired or was already used. Please start sign-in again.');
        return;
      }

      // For network errors / 500s only — retry up to 2× with short backoff
      // (the upstream Emergent Auth service occasionally has transient blips)
      if (attemptNum < 2 && (!status || status >= 500)) {
        setAttempt(attemptNum);
        await new Promise(r => setTimeout(r, 1500));
        return processAuth(sessionId, attemptNum + 1);
      }

      setError('Sign-in service temporarily unavailable. Please try again in a moment.');
    }
  };

  // For network/500 errors we can re-try the SAVED session_id; for "expired"
  // (no saved id) we must restart the OAuth flow from /login.
  const handleManualRetry = async () => {
    const savedSessionId = sessionStorage.getItem('pending_session_id');
    if (savedSessionId) {
      setError(null);
      setRetrying(true);
      setAttempt(0);
      await processAuth(savedSessionId, 1);
      setRetrying(false);
    } else {
      navigate('/login', { replace: true });
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      // Check sessionStorage for a pending session (page reload recovery)
      const savedSessionId = sessionStorage.getItem('pending_session_id');
      if (savedSessionId) {
        processAuth(savedSessionId, 1);
        return;
      }
      navigate('/login', { replace: true });
      return;
    }

    // Save session_id for retry/recovery before processing
    sessionStorage.setItem('pending_session_id', sessionId);
    processAuth(sessionId, 1);
  }, [navigate, login, setUserFromCallback]);

  // Show error with retry option
  if (error) {
    const is403 = error.includes('not authorized');
    const isExpired = error.includes('expired') || error.includes('already used');
    const canRetryInPlace = !is403 && !isExpired;
    return (
      <div className="min-h-screen bg-forest-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-sm shadow-xl p-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-sm flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-center text-foreground mb-2">
              {is403 ? 'Access Denied' : isExpired ? 'Sign-In Link Expired' : 'Sign-In Issue'}
            </h2>
            <p className="text-sm text-muted-foreground text-center mb-6">
              {error}
            </p>
            {is403 ? (
              <p className="text-xs text-muted-foreground text-center mb-6">
                Please contact the administrator to request access.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground text-center mb-6">
                {isExpired
                  ? 'Click below to sign in again. If this keeps happening, try the "Sign in with Email" option.'
                  : 'Please try again. If the problem persists, use "Sign in with Email" instead.'}
              </p>
            )}
            <div className="space-y-3">
              {canRetryInPlace && (
                <Button
                  onClick={handleManualRetry}
                  disabled={retrying}
                  className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                  data-testid="auth-retry-btn"
                >
                  {retrying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Retrying...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Try Again</>
                  )}
                </Button>
              )}
              {isExpired && (
                <Button
                  onClick={() => {
                    sessionStorage.removeItem('pending_session_id');
                    navigate('/login', { replace: true });
                  }}
                  className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                  data-testid="auth-restart-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Restart Sign-in
                </Button>
              )}
              <Button
                onClick={() => {
                  sessionStorage.removeItem('pending_session_id');
                  navigate('/login', { replace: true });
                }}
                variant="outline"
                className="w-full rounded-sm"
                data-testid="auth-back-to-login-btn"
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forest-900 flex items-center justify-center">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 bg-terracotta rounded-sm flex items-center justify-center">
            <Map className="w-7 h-7 text-white" />
          </div>
        </div>
        <Loader2 className="w-8 h-8 text-white animate-spin mx-auto mb-4" />
        <p className="text-white/60 text-sm">
          {attempt > 0 ? `Retrying... (attempt ${attempt + 1})` : 'Signing you in...'}
        </p>
      </div>
    </div>
  );
};
